import { List } from "react-native-paper";
import type { IconType } from "../../icon";
import { useAppTheme } from "../../useAppTheme";
import { AppIcon } from "../AppIcon";
import Box from "../Box";
import { LevelChip, type LevelChipProps } from "../LevelChip";
import { ScoreChip, type ScoreChipProps } from "../ScoreChip";
import TooltipWrapper from "../TooltipWrapper";

export type ItemTypeListItemProps = {
	icon: IconType;
	title: string;
	subtitle?: string;
	tooltip?: string;
	level?: LevelChipProps;
	score?: Omit<ScoreChipProps, "minWidth">;
	onPress?: () => void;
};

const ItemTypeListItemTrailing = ({
	level,
	score,
	tooltip,
	showChevron,
}: {
	level?: LevelChipProps;
	score?: Omit<ScoreChipProps, "minWidth">;
	tooltip?: string;
	showChevron: boolean;
}) => {
	const theme = useAppTheme();
	const hasChips = Boolean(level) || Boolean(score);

	const chips = hasChips ? (
		<Box flexDirection="row" alignItems="center" gap={8}>
			{level ? <LevelChip {...level} /> : null}
			{score ? <ScoreChip {...score} /> : null}
		</Box>
	) : null;

	return (
		<Box flexDirection="row" alignItems="center" alignSelf="center" gap={4}>
			{tooltip && chips ? (
				<TooltipWrapper title={tooltip}>{chips}</TooltipWrapper>
			) : (
				chips
			)}
			{showChevron ? (
				<AppIcon
					name="chevron-right"
					size={24}
					color={theme.colors.onSurfaceVariant}
				/>
			) : null}
		</Box>
	);
};

export const ItemTypeListItem = ({
	icon,
	title,
	subtitle,
	tooltip,
	level,
	score,
	onPress,
}: ItemTypeListItemProps) => {
	const theme = useAppTheme();
	const showRight = Boolean(level) || Boolean(score) || Boolean(onPress);

	return (
		<List.Item
			onPress={onPress}
			style={{ paddingHorizontal: 16, paddingVertical: 8 }}
			left={() => (
				<Box
					width={32}
					height={32}
					alignItems="center"
					justifyContent="center"
					alignSelf="center"
				>
					<AppIcon name={icon} size={24} color={theme.colors.onSurface} />
				</Box>
			)}
			contentStyle={{ paddingHorizontal: 8, gap: 6 }}
			title={title}
			titleNumberOfLines={1}
			titleStyle={{ fontSize: 14, fontWeight: 600 }}
			description={subtitle}
			descriptionStyle={{ fontSize: 12 }}
			descriptionNumberOfLines={1}
			right={
				showRight
					? () => (
							<ItemTypeListItemTrailing
								level={level}
								score={score}
								tooltip={tooltip}
								showChevron={Boolean(onPress)}
							/>
						)
					: undefined
			}
		/>
	);
};
